import Queue from "nq";
import { getAllSchemasUseCase } from "../schema/usecases";
import { delay, isRetryableError } from "./retryUtils";

let cachedSchemas = null;
let schemasLoadPromise = null;

function loadSchemas() {
  if (cachedSchemas) return Promise.resolve(cachedSchemas);
  if (!schemasLoadPromise) {
    schemasLoadPromise = getAllSchemasUseCase()
      .execute()
      .then((schemas) => {
        cachedSchemas = schemas?.length ? schemas : [];
        return cachedSchemas;
      })
      .catch((err) => {
        schemasLoadPromise = null;
        throw err;
      });
  }
  return schemasLoadPromise;
}

async function getSchema(collection) {
  const schemas = await loadSchemas();
  return schemas.find((s) => s.collection === collection);
}

const SKIP_POINTER_NORMALIZE = new Set(["schedule_template", "employees"]);

function normalizePointers(object, fields, collection) {
  const skipPointers = SKIP_POINTER_NORMALIZE.has(collection);

  for (const field of Object.keys(object)) {
    const fieldDef = fields?.[field];
    if (!fieldDef) continue;

    const val = object[field];
    if (val == null) continue;

    if (fieldDef.type === "Pointer" && !skipPointers) {
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        val.id != null &&
        Object.keys(val).length > 1
      ) {
        object[field] = { id: val.id };
      }
    } else if (
      fieldDef.type === "Relation" &&
      !fieldDef._type &&
      Array.isArray(val)
    ) {
      const needsNormalize = val.some(
        (item) =>
          item && typeof item === "object" && Object.keys(item).length > 1,
      );
      if (needsNormalize) {
        object[field] = val.map((item) =>
          item?.id != null ? { id: item.id } : item,
        );
      }
    }
  }

  return object;
}

const documentClient = new Queue.Document();

/** Scale in-flight requests by total record count. Kept conservative to avoid connection drops. */
export function resolveBulkConcurrency(totalCount) {
  if (totalCount <= 50) return 10;
  if (totalCount <= 200) return 15;
  if (totalCount <= 1000) return 20;
  if (totalCount <= 2000) return 25;
  return 30;
}

class UpsertUseCase {
  upsertNormalized(collection, object) {
    if (object.id) {
      const options = { params: { upsert: true } };
      return documentClient.update(collection, object, options);
    }

    return documentClient.create(collection, object);
  }

  async upsertOne(collection, object, fields) {
    const normalized = normalizePointers({ ...object }, fields, collection);
    return this.upsertNormalized(collection, normalized);
  }

  async execute(collection, object) {
    const schema = await getSchema(collection);
    return this.upsertOne(collection, object, schema?.fields);
  }

  async upsertWithRetry(collection, object, maxRetries = 3) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.upsertNormalized(collection, object);
      } catch (error) {
        if (!isRetryableError(error) || attempt === maxRetries) {
          throw error;
        }
        await delay(750 * (attempt + 1));
      }
    }
  }

  async executeMany(collection, objects, options = {}) {
    if (!objects?.length) return [];

    const concurrency =
      options.concurrency ?? resolveBulkConcurrency(objects.length);
    const {
      onProgress,
      progressInterval = 50,
      maxRetries = 3,
      continueOnItemError = false,
      skipNormalize = false,
    } = options;

    let normalizedObjects;
    if (skipNormalize) {
      normalizedObjects = objects;
    } else {
      const schema = await getSchema(collection);
      const fields = schema?.fields;
      normalizedObjects = objects.map((object) =>
        normalizePointers({ ...object }, fields, collection)
      );
    }

    const results = new Array(normalizedObjects.length);
    let nextIndex = 0;
    let completed = 0;
    const failedIndexes = [];
    let lastError;
    const workerCount = Math.min(concurrency, normalizedObjects.length);

    const runWorker = async () => {
      while (nextIndex < normalizedObjects.length) {
        const index = nextIndex++;
        try {
          results[index] = await this.upsertWithRetry(
            collection,
            normalizedObjects[index],
            maxRetries
          );
        } catch (error) {
          if (!continueOnItemError) throw error;
          failedIndexes.push(index);
          lastError = error;
        }

        completed++;
        if (
          onProgress &&
          (completed % progressInterval === 0 ||
            completed === normalizedObjects.length)
        ) {
          onProgress(completed, normalizedObjects.length, {
            failedCount: failedIndexes.length,
          });
        }
      }
    };

    await Promise.all(
      Array.from({ length: workerCount }, () => runWorker())
    );

    if (failedIndexes.length) {
      const error =
        lastError ||
        new Error(`Failed to upsert ${failedIndexes.length} record(s)`);
      error.failedIndexes = failedIndexes.sort((a, b) => a - b);
      throw error;
    }

    return results;
  }
}

export default UpsertUseCase;
